import { useState } from "react";
import { Plus } from "lucide-react";
import { api, type QuoteDto } from "../../lib/api";
import { formatINR } from "../../lib/format";
import {
  ActionMessage,
  Button,
  Card,
  Checkbox,
  Field,
  Input,
  Modal,
  MoneyInput,
  PanelSection,
  RowActions,
  Select,
  useAdminAction,
} from "./shared";

interface QuoteForm {
  title: string;
  vendorName: string;
  channel: string;
  category: string;
  basePricePaise: number | null;
  taxInclusive: boolean;
  gstRatePercent: string;
  gstAmountPaise: number | null;
  convenienceFeePaise: number | null;
  discountPaise: number | null;
  otherChargesPaise: number | null;
  finalTotalPaise: number | null;
  notes: string;
  quoteRef: string;
}

const emptyForm: QuoteForm = {
  title: "",
  vendorName: "",
  channel: "offline",
  category: "food",
  basePricePaise: 0,
  taxInclusive: false,
  gstRatePercent: "",
  gstAmountPaise: null,
  convenienceFeePaise: 0,
  discountPaise: 0,
  otherChargesPaise: 0,
  finalTotalPaise: null,
  notes: "",
  quoteRef: "",
};

export default function QuotesPanel({ quotes, onSaved }: { quotes: QuoteDto[]; onSaved: () => void }) {
  const { message, busy, run } = useAdminAction();
  const [target, setTarget] = useState<"new" | QuoteDto | null>(null);
  const [form, setForm] = useState<QuoteForm>(emptyForm);

  const openEdit = (q: QuoteDto | "new") => {
    if (q === "new") {
      setForm(emptyForm);
    } else {
      setForm({
        title: q.title,
        vendorName: q.vendorName,
        channel: q.channel,
        category: q.category,
        basePricePaise: q.breakdown.basePricePaise,
        taxInclusive: q.breakdown.taxInclusive,
        gstRatePercent: q.breakdown.gstBps !== null ? String(q.breakdown.gstBps / 100) : "",
        gstAmountPaise: q.breakdown.gstSource === "quoted_amount" ? q.breakdown.gstPaise : null,
        convenienceFeePaise: q.breakdown.convenienceFeePaise,
        discountPaise: q.breakdown.discountPaise,
        otherChargesPaise: q.breakdown.otherChargesPaise,
        finalTotalPaise: q.breakdown.quotedFinalTotalPaise,
        notes: q.notes,
        quoteRef: q.quoteRef,
      });
    }
    setTarget(q);
  };

  const save = () =>
    run(async () => {
      const payload = {
        title: form.title,
        vendorName: form.vendorName,
        channel: form.channel,
        category: form.category,
        basePricePaise: form.basePricePaise ?? 0,
        taxInclusive: form.taxInclusive,
        gstRateBps: form.gstRatePercent ? Math.round(Number(form.gstRatePercent) * 100) : null,
        gstAmountPaise: form.gstAmountPaise,
        convenienceFeePaise: form.convenienceFeePaise ?? 0,
        discountPaise: form.discountPaise ?? 0,
        otherChargesPaise: form.otherChargesPaise ?? 0,
        finalTotalPaise: form.finalTotalPaise,
        notes: form.notes,
        quoteRef: form.quoteRef,
      };
      if (target === "new") await api.adminSend("/quotes", "POST", payload);
      else if (target) await api.adminSend(`/quotes/${target.id}`, "PUT", payload);
      setTarget(null);
      onSaved();
    }, "Vendor quote saved.");

  return (
    <PanelSection
      title="Vendor quotations (online vs offline)"
      description="Record each quotation exactly as the vendor stated it. GST comes from the quote's own tax fields only — never from whether the quote is online or offline."
      action={
        <Button onClick={() => openEdit("new")}>
          <Plus className="h-4 w-4" aria-hidden="true" /> Add quotation
        </Button>
      }
    >
      <div className="space-y-3">
        {quotes.map((q) => (
          <Card key={q.id} className="p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="font-medium text-white">
                  {q.title}{" "}
                  <span className="text-xs text-wandor-muted">
                    ({q.channel} · {q.category})
                  </span>
                </p>
                <p className="text-sm text-wandor-muted">
                  Base {formatINR(q.breakdown.basePricePaise)} · GST{" "}
                  {q.breakdown.gstSource === "none"
                    ? "not quoted"
                    : `${formatINR(q.breakdown.gstPaise)} ${q.breakdown.taxInclusive ? "(inclusive)" : "(exclusive)"}`}{" "}
                  · Fees {formatINR(q.breakdown.convenienceFeePaise)} · Discount{" "}
                  {formatINR(q.breakdown.discountPaise)} · Final{" "}
                  <strong className="text-white">{formatINR(q.breakdown.finalTotalPaise)}</strong>
                </p>
                {q.quoteRef && <p className="text-xs text-white/40">Ref: {q.quoteRef}</p>}
              </div>
              <RowActions
                onEdit={() => openEdit(q)}
                onDelete={() => {
                  if (window.confirm(`Delete quote “${q.title}”?`)) {
                    void run(async () => {
                      await api.adminSend(`/quotes/${q.id}`, "DELETE");
                      onSaved();
                    }, "Quote deleted.");
                  }
                }}
              />
            </div>
          </Card>
        ))}
      </div>

      <ActionMessage message={message} />

      {target && (
        <Modal title={target === "new" ? "Add vendor quotation" : `Edit “${target.title}”`} onClose={() => setTarget(null)}>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <Field label="Quote title" htmlFor="q-title">
              <Input id="q-title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} maxLength={200} />
            </Field>
            <Field label="Vendor name" htmlFor="q-vendor">
              <Input id="q-vendor" value={form.vendorName} onChange={(e) => setForm((f) => ({ ...f, vendorName: e.target.value }))} maxLength={160} />
            </Field>
            <Field label="Channel" htmlFor="q-channel">
              <Select id="q-channel" value={form.channel} onChange={(e) => setForm((f) => ({ ...f, channel: e.target.value }))}>
                <option value="online">Online</option>
                <option value="offline">Offline</option>
              </Select>
            </Field>
            <Field label="Category" htmlFor="q-cat">
              <Select id="q-cat" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}>
                <option value="food">Food</option>
                <option value="venue">Venue</option>
                <option value="transport">Transport</option>
                <option value="other">Other</option>
              </Select>
            </Field>
            <MoneyInput label="Base price (₹)" paise={form.basePricePaise} onChangePaise={(p) => setForm((f) => ({ ...f, basePricePaise: p }))} />
            <Field label="GST rate (%, as stated on the quote)" htmlFor="q-gst-rate">
              <Input id="q-gst-rate" type="number" min={0} max={28} step="0.01" value={form.gstRatePercent} onChange={(e) => setForm((f) => ({ ...f, gstRatePercent: e.target.value }))} />
            </Field>
            <MoneyInput
              label="GST amount (₹, if the quote states one)"
              paise={form.gstAmountPaise}
              onChangePaise={(p) => setForm((f) => ({ ...f, gstAmountPaise: p }))}
              hint="Overrides the rate-derived amount."
            />
            <MoneyInput label="Payment/convenience fee (₹)" paise={form.convenienceFeePaise} onChangePaise={(p) => setForm((f) => ({ ...f, convenienceFeePaise: p }))} />
            <MoneyInput label="Discount (₹)" paise={form.discountPaise} onChangePaise={(p) => setForm((f) => ({ ...f, discountPaise: p }))} />
            <MoneyInput label="Other quoted charges (₹)" paise={form.otherChargesPaise} onChangePaise={(p) => setForm((f) => ({ ...f, otherChargesPaise: p }))} />
            <MoneyInput
              label="Final payable total (₹, as quoted)"
              paise={form.finalTotalPaise}
              onChangePaise={(p) => setForm((f) => ({ ...f, finalTotalPaise: p }))}
              hint="Leave empty to compute from the components above."
            />
            <Field label="Quote reference" htmlFor="q-ref">
              <Input id="q-ref" value={form.quoteRef} onChange={(e) => setForm((f) => ({ ...f, quoteRef: e.target.value }))} maxLength={200} />
            </Field>
            <div className="sm:col-span-2">
              <Checkbox
                label="Quote is tax-inclusive (GST included in the base price)"
                checked={form.taxInclusive}
                onChange={(e) => setForm((f) => ({ ...f, taxInclusive: e.target.checked }))}
              />
            </div>
            <div className="sm:col-span-2">
              <Field label="Notes">
                <textarea
                  className="w-full min-h-[60px] rounded-xl border border-white/15 bg-white/[0.04] px-4 py-2.5 text-sm outline-none focus:border-wandor-prompt"
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  maxLength={2000}
                />
              </Field>
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setTarget(null)}>Cancel</Button>
            <Button onClick={save} loading={busy}>Save quotation</Button>
          </div>
        </Modal>
      )}
    </PanelSection>
  );
}
